<template>
  <ItemsPage
    id="market"
    v-slot="slotProps"
    :class="{ 'not-found': !items.length }"
    :form="form"
    :filters="filters_all[app_id]"
    :allow-family="true"
    :family="family_active"
    :saved="true"
    :discount="true"
  >
    <ItemsFeatured />
    <ContentBar class="main">
      <template #left>
        <GameSelect class="desktop-none" />
        <div class="breadcrumbs flex-row tablet-none mobile-none">
          <button class="link-first flex-row">
            <div class="img-game flex">
              <img :src="game_details.bar_icon || game_details.icon" alt="selected game">
            </div>
            <span class="fw-m">{{ game_details.name }}</span>
          </button>
        </div>
      </template>
      <template #right>
        <div v-if="show_snowfall" class="snowfall-show">
          <BamWrapper :keep-space="false" class="switch trade-hold" @click="setAnimationSnowfall">
            <span v-tooltip="{ message: $t('market.show_snowfall'), position: 'center top' }" class="market-snowflake">
              <img src="@/asset/img/x-mas/snowflake.svg" alt="icon">
            </span>
            <div class="form-element base-input switch-box">
              <input aria-label="Toggle snow" type="checkbox" :checked="show_animation_snowfall"> <span class="slider" />
            </div>
          </BamWrapper>
        </div>
        <div class="market-show">
          <BamWrapper :keep-space="false" class="switch trade-hold" @click="setItemsOpened">
            <span v-tooltip="{ message: $t('market.show_item_menu'), position: 'center top' }" class="market-show-dots">
              <img src="@/asset/icon/dots.svg" :alt="$t('market.show_item_menu')">
            </span>
            <div class="form-element base-input switch-box">
              <input aria-label="Toggle menu" type="checkbox" :checked="show_item_menu"> <span class="slider" />
            </div>
          </BamWrapper>
        </div>
        <Sorting :form="form" :family="family_sorting" />
        <button class="btn btn-primary btn-filter" @click="toggleFilters">
          <img src="@/asset/icon/filter.svg" alt="sorting">
        </button>
      </template>
    </ContentBar>
    <div v-if="form.is_loading.value || !form.is_called.value" class="splash-sticky-wrapper" :style="{ height: last_height + 'px' }">
      <div class="splash-sticky">
        <div class="icon spinning">
          <img src="@/asset/icon/magazine.svg" alt="loader">
        </div>
        <div class="title">{{ $t('common.loading') }}</div>
      </div>
    </div>
    <div v-else-if="form.api_error.value" class="splash error">
      <div class="title">{{ $t('common.error') }}</div>
      <div class="description">
        <BamError :error="$to(form.api_error.value)" />
      </div>
    </div>
    <div v-else-if="!items.length" class="no-items-found">
      <div class="splash gray">
        <img src="@/asset/img/not-found.png" alt="No items found">
        <div class="wrapper">
          <h1 v-html="$t('market.no_items_found.no_items_found_span')" />
          <p class="search-name">
            {{ $t('market.no_items_found.we_could_not_find_any_offers') }}
            <template v-if="searched_skin_name"> {{ $t('market.no_items_found.for') }} <span class="skin-name">{{ searched_skin_name }}</span></template>
          </p>
          <p>{{ $t('market.no_items_found.this_can_be_due_to_applied_filters') }}</p>
          <button class="btn btn-primary" @click="slotProps.resetAll">
            <span>{{ $t('market.filter.clear_all_filters') }}</span>
          </button>
        </div>
      </div>
      <ItemsFeaturedSmall />
    </div>
    <div v-else ref="market_items" class="market-items">
      <ItemMarket
        v-for="item in items"
        :key="item.asset_id"
        :item="item"
      />
    </div>
    <Pagination
      v-if="!(form.is_loading.value || !form.is_called.value || form.api_error.value)"
      ref="market_pagination"
      class="pagination bottom"
      v-bind="form.usePagination(items_limit, false)"
      :submit-action="form.onSubmit"
      :summary="true"
      @input="scrollUp"
    />
  </ItemsPage>
</template>

<script setup>
import { api } from '@/facade/api.js';
import { main_bus } from '@/hook/bus.js';
import { game_details, setGameBySlug, app_id, getBySlug } from '@/hook/market/game.js';
import { toggleFilters } from '@/hook/mobile/filters.js';
import { router } from '@/main.js';
import { meta, PATH_NAME_ENUM } from '@common/meta.js';
import { API_ROUTE } from '@common/schema/api.js';
import { EVENT_BUS } from 'bambam-bus';
import { isSameArrays } from 'bambam-util/cherrypick.js';
import { computed, ref, watch } from 'vue';
import { changeGame, form, items, filters as filters_all } from '@/hook/market/items.js';
import { items_limit } from '@/hook/market/inventory.js';
import { useSettings } from '@/hook/settings.js';
import { useSnowfall } from '@/hook/snowfall.js';
import { BamWrapper } from 'bambam-form';
import ItemsFeatured from '@/component/market/ItemsFeatured.vue';
import ContentBar from '@/component/items-page/ContentBar.vue';
import ItemsFeaturedSmall from '@/component/market/ItemsFeaturedSmall.vue';
import GameSelect from '@/component/body/GameSelect.vue';
import Pagination from 'bambam-component-core/Pagination.vue';
import BamError from 'bambam-form/component/BamError.vue';
import ItemsPage from '@/view/ItemsPage.vue';
import Sorting from '@/component/market/Sorting.vue';
import ItemMarket from '@/component/market/ItemMarket.vue';

const searched_skin_name = ref(null);

const props = defineProps({
  game: {
    type: String,
    required: true,
  },
  custom: {
    type: String,
    default: null,
  },
  skin: {
    type: String,
    default: null,
  },
  collapsed: {
    type: Boolean,
    default: false,
  },
});

const market_items = ref();
const market_pagination = ref();

const last_height = ref(0);
watch(() => market_items.value, (v) => {
  if (v) {
    const { height } = v.getBoundingClientRect();
    const { height: pagination_height } = market_pagination.value.$el.getBoundingClientRect();
    last_height.value = height + (pagination_height || 40);
  }
});

watch(() => props.game, async () => {
  setGameBySlug(props.game);
  changeGame();
}, { immediate: true });

// function areArrayContainsSame(array1, array2) {
//   if (array1.length === array2.length) {
//     return array1.every(element => !!array2.includes(element));
//   }
//   return false;
// }

const family_sorting = computed(() => {
  const families = game_details.value?.family;
  const active = form.value.getValue('where.phase_id');
  if (families && active) {
    return Object.values(families).find((o) => isSameArrays(o.value, active));
  }
  return null;
});

const family_active = computed(() => {
  return null; // FAMILY ACTIONS DISABLED

  // const filters = filters_all[app_id.value];
  // if (!form.value || !game_details.value?.family) {
  //   return null;
  // }
  //
  // const active = form.value.getValue('where.phase_id');
  // if (!active) {
  //   return null;
  // }
  //
  // const active_details = Object.values(game_details.value.family).find((o) => areArrayContainsSame(o.value, active));
  // console.log(active, active_details, active_details.filters);
  //
  // return {
  //   ...active_details,
  //   icons: active_details?.phases?.map((v) => getIcon(app_id.value, 'phase', v)) || [],
  // };
});

const scrollUp = () => {
  const $elem = document.querySelector('#featured-items');
  if ($elem) {
    $elem?.scrollIntoView();
  }
};

const cached = {};
const getSkin = async (skin, game) => {
  if (cached[skin]) {
    return cached[skin];
  }
  if (skin) {
    const data = await api.call(API_ROUTE.market.search.skin_name, {
      where: {
        app_id: getBySlug(game),
        id: [Number(skin)],
      },
      limit: 1,
    });
    cached[skin] = data?.[0];
    return cached[skin];
  }
  return null;
};

const updateMeta = async () => {
  const route = router.currentRoute.value;
  const { params, query } = route;
  let search = null;
  try {
    search = JSON.parse(query.search);
    searched_skin_name.value = (search?.where?.skin_name || '').replace(/%/g, ' ').trim();
  } catch (e) {
    // invalid search
  }
  const { locale, game, family, skin } = params;
  const data = meta.getBuilder(PATH_NAME_ENUM.market)({
    locale, game, family, search, skin: await getSkin(skin, game),
  });
  const title = data?.[`title_${params.locale}`] || data?.title;
  window.document.title = title;
};
watch(router.currentRoute, updateMeta, { immediate: true });
main_bus.on(EVENT_BUS.changedLocale, updateMeta);

const { setItemsOpened, show_item_menu } = useSettings();
const { setAnimationSnowfall, show_animation_snowfall, show_snowfall } = useSnowfall();
</script>
